import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';
import { Aum } from '@pinnakl/poems/aum/domain';
import { withDevtools } from '@pinnakl/shared/signal-store';
import { AumApiService } from './aum-api.service';

interface AumState {
  aums: Aum[];
  loading: boolean;
  loaded: boolean;
}

const initialState: AumState = {
  aums: [],
  loading: false,
  loaded: false,
};

export const AumStore = signalStore(
  { providedIn: 'root' },
  withDevtools('aums'),
  withState(initialState),
  withProps(store => ({
    aums$: toObservable(store.aums),
    loading$: toObservable(store.loading),
  })),
  withMethods((store, api = inject(AumApiService)) => {
    let pending: Promise<void> | null = null;

    const load = async (): Promise<void> => {
      patchState(store, { loading: true });
      try {
        const aums = await api.getAums();
        patchState(store, { aums, loading: false, loaded: true });
      } catch {
        patchState(store, { loading: false });
      } finally {
        pending = null;
      }
    };

    return {
      loadAums(forceRefetch = false): Promise<void> {
        if (pending) return pending;
        if (!forceRefetch && store.loaded()) return Promise.resolve();
        pending = load();
        return pending;
      },
      setAums(aums: Aum[]): void {
        patchState(store, { aums, loading: false, loaded: true });
      },
    };
  }),
);
